/**
 * ExecutionSummary - Compact summary of connector execution
 *
 * Displays key metrics from a connector execution including status,
 * timing, records processed, and data written.
 */
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ExecutionStatusBadge } from './ExecutionStatusBadge';
import { cn } from '@/lib/utils';
import type { ConnectorExecution } from '@/types/connectors';
import { formatDuration } from '@/utils/formatDuration';

export interface ExecutionSummaryProps {
  execution: ConnectorExecution;
  className?: string;
  showFullTimestamp?: boolean;
}


/**
 * Format number with commas (e.g., 1234567 -> 1,234,567)
 */
function formatNumber(num: number | null | undefined): string {
  if (num == null) return '0';
  return num.toLocaleString('en-US');
}

/**
 * Format bytes to human-readable size (KB, MB, GB)
 */
function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/**
 * ExecutionSummary component
 *
 * @example
 * <ExecutionSummary execution={latestExecution} />
 */
export function ExecutionSummary({
  execution,
  className,
  showFullTimestamp = false,
}: ExecutionSummaryProps) {
  // Force re-render every 30s so relative time text stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const relativeTime = execution.started_at
    ? formatDistanceToNow(new Date(execution.started_at), { addSuffix: true })
    : null;

  const fullTimestamp = execution.started_at
    ? new Date(execution.started_at).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div
      className={cn(
        'rounded-lg border bg-muted/50 p-3 space-y-2',
        className
      )}
    >
      {/* Header: Status and Time */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ExecutionStatusBadge status={execution.status} size="sm" />
          {relativeTime && (
            <span
              className="text-xs text-muted-foreground"
              title={fullTimestamp || undefined}
            >
              {showFullTimestamp ? fullTimestamp : relativeTime}
            </span>
          )}
        </div>
        {execution.duration_seconds !== undefined && (
          <span className="text-xs font-medium text-muted-foreground">
            Duration: {formatDuration(execution.duration_seconds, { emptyText: '—', zeroIsEmpty: true })}
          </span>
        )}
      </div>

      {/* Metrics Row */}
      <div className="flex items-center gap-4 text-sm">
        {/* Records Processed */}
        {execution.records_processed > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-foreground">
              {formatNumber(execution.records_processed)}
            </span>
            <span className="text-muted-foreground">records</span>
          </div>
        )}

        {/* Tables Updated or Files Created */}
        {execution.tables_updated > 0 ? (
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-foreground">
              {execution.tables_updated}
            </span>
            <span className="text-muted-foreground">
              {execution.tables_updated === 1 ? 'table' : 'tables'}
            </span>
          </div>
        ) : execution.files_created > 0 ? (
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-foreground">
              {execution.files_created}
            </span>
            <span className="text-muted-foreground">
              {execution.files_created === 1 ? 'file' : 'files'}
            </span>
          </div>
        ) : null}

        {/* Bytes Written */}
        {execution.bytes_written > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-foreground">
              {formatBytes(execution.bytes_written)}
            </span>
            <span className="text-muted-foreground">written</span>
          </div>
        )}
      </div>

      {/* Error Message (if failed) */}
      {execution.status === 'failed' && execution.error_message && (
        <div className="text-xs text-destructive pt-1 border-t">
          {execution.error_message.length > 100
            ? `${execution.error_message.slice(0, 97)}...`
            : execution.error_message}
        </div>
      )}
    </div>
  );
}

/**
 * Empty state when no execution history exists
 */
export function ExecutionSummaryEmpty({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-dashed bg-muted/30 p-3',
        'flex items-center gap-2 text-sm text-muted-foreground',
        className
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>No execution history</span>
      <span className="text-xs">Run this connector to see metrics</span>
    </div>
  );
}
